import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { PageLayout } from '../../components/layout/PageLayout';

export default function LeadDetail() {
  const { email } = useParams<{ email: string }>();
  const [lead, setLead] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Status controls
  const [leadStatus, setLeadStatus] = useState('');
  const [nmiStatus, setNmiStatus] = useState('');
  const [bankingStatus, setBankingStatus] = useState('');
  const [feeScheduleStatus, setFeeScheduleStatus] = useState('');
  const [tapToPayStatus, setTapToPayStatus] = useState('');
  const [merchantActivationStatus, setMerchantActivationStatus] = useState('');
  const [onboardingEmailStatus, setOnboardingEmailStatus] = useState('');
  const [tapToPayLaunchTool, setTapToPayLaunchTool] = useState('PENDING_CONFIRMATION');
  const [tapToPayLaunchUrl, setTapToPayLaunchUrl] = useState('');
  const [tapToPayNotes, setTapToPayNotes] = useState('');
  
  // Track original statuses to detect changes
  const [originalMerchantActivationStatus, setOriginalMerchantActivationStatus] = useState('');
  const [originalTapToPayStatus, setOriginalTapToPayStatus] = useState('');

  useEffect(() => {
    if (email) fetchLead(decodeURIComponent(email));
  }, [email]);

  async function fetchLead(contactEmail: string) {
    if (!supabase) return;
    
    const { data, error } = await supabase
      .from('paygent_leads')
      .select('*')
      .eq('contact_email', contactEmail)
      .single();
      
    if (data) {
      setLead(data);
      setLeadStatus(data.lead_status || '');
      setNmiStatus(data.nmi_creation_status || '');
      setBankingStatus(data.banking_status || '');
      setFeeScheduleStatus(data.fee_schedule_status || '');
      setTapToPayStatus(data.tap_to_pay_status || '');
      setOriginalTapToPayStatus(data.tap_to_pay_status || '');
      setMerchantActivationStatus(data.merchant_activation_status || 'NOT_STARTED');
      setOriginalMerchantActivationStatus(data.merchant_activation_status || 'NOT_STARTED');
      setOnboardingEmailStatus(data.onboarding_email_status || 'NOT_SENT');
      setTapToPayLaunchTool(data.tap_to_pay_launch_tool || 'PENDING_CONFIRMATION');
      setTapToPayLaunchUrl(data.tap_to_pay_launch_url || '');
      setTapToPayNotes(data.tap_to_pay_notes || '');
    }
    setLoading(false);
  }

  async function handleSaveStatus() {
    if (!supabase || !lead) return;
    setSaving(true);
    
    const { error } = await supabase
      .from('paygent_leads')
      .update({
        lead_status: leadStatus,
        nmi_creation_status: nmiStatus,
        banking_status: bankingStatus,
        fee_schedule_status: feeScheduleStatus,
        tap_to_pay_status: tapToPayStatus,
        merchant_activation_status: merchantActivationStatus,
        onboarding_email_status: onboardingEmailStatus,
        tap_to_pay_launch_tool: tapToPayLaunchTool,
        tap_to_pay_launch_url: tapToPayLaunchUrl,
        tap_to_pay_notes: tapToPayNotes
      })
      .eq('contact_email', lead.contact_email);
      
    if (!error) {
      // Trigger Tapn status update notifications if changed
      const tapnApiBase = 'https://www.tapndone.com';
      
      if (merchantActivationStatus !== originalMerchantActivationStatus) {
        fetch(`${tapnApiBase}/api/notifications/status-update`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: lead.contact_email,
            business_name: lead.business_name,
            name: lead.first_name,
            previous_status: originalMerchantActivationStatus,
            new_status: merchantActivationStatus,
            status_type: 'merchant_activation'
          })
        }).catch(err => console.error("Notification trigger failed:", err));
        setOriginalMerchantActivationStatus(merchantActivationStatus);
      }

      if (tapToPayStatus !== originalTapToPayStatus) {
        fetch(`${tapnApiBase}/api/notifications/status-update`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: lead.contact_email,
            business_name: lead.business_name,
            name: lead.first_name,
            previous_status: originalTapToPayStatus,
            new_status: tapToPayStatus,
            status_type: 'tap_to_pay'
          })
        }).catch(err => console.error("Notification trigger failed:", err));
        setOriginalTapToPayStatus(tapToPayStatus);
      }

      alert('Statuses updated successfully.');
    } else {
      alert('Error updating statuses.');
    }
    setSaving(false);
  }

  async function handleSendOnboardingInstructions() {
    if (!supabase || !lead) return;
    
    const emailTemplate = `Subject: Complete Your Paygent Merchant Activation

Hi ${lead.first_name || lead.business_name},

Paygent received your request. Your Paygent Standard plan request has been recorded.

Tapn / Tap to Pay has been requested but requires Apple Pay / Google Pay / SoftPOS service enablement and terms acceptance.

Secure merchant activation will be completed through Paygent/NMI.
Do not send banking information by regular email.
Paygent will provide a secure completion method or portal instructions shortly.

If you need help, contact Paygent Support at support@gopaygent.com.

Welcome aboard!
The Paygent Team`;

    navigator.clipboard.writeText(emailTemplate);
    alert("Template copied to clipboard!");

    const now = new Date().toISOString();
    
    // Update local state
    setOnboardingEmailStatus('SENT');
    
    // Update Supabase
    await supabase
      .from('paygent_leads')
      .update({
        onboarding_email_status: 'SENT',
        onboarding_email_sent_at: now
      })
      .eq('contact_email', lead.contact_email);
      
    // Refresh lead to show timestamp
    fetchLead(lead.contact_email);
  }

  if (loading) return <div className="p-10 text-center">Loading lead details...</div>;
  if (!lead) return <div className="p-10 text-center">Lead not found.</div>;

  return (
    <PageLayout
      title="Lead Detail"
      subtitle={`Managing ${lead.business_name}`}
      badge="INTERNAL ADMIN"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Left Column: Details & Status Controls */}
        <div className="space-y-8">
          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-blue-900/5">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Lead Information</h3>
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 border-b border-gray-50 pb-2">
                <span className="text-gray-500">Business Name</span>
                <span className="col-span-2 font-medium">{lead.business_name}</span>
              </div>
              <div className="grid grid-cols-3 border-b border-gray-50 pb-2">
                <span className="text-gray-500">Contact Name</span>
                <span className="col-span-2 font-medium">{lead.first_name} {lead.last_name}</span>
              </div>
              <div className="grid grid-cols-3 border-b border-gray-50 pb-2">
                <span className="text-gray-500">Email</span>
                <span className="col-span-2 font-medium">{lead.contact_email}</span>
              </div>
              <div className="grid grid-cols-3 border-b border-gray-50 pb-2">
                <span className="text-gray-500">Phone</span>
                <span className="col-span-2 font-medium">{lead.phone || 'N/A'}</span>
              </div>
              <div className="grid grid-cols-3 border-b border-gray-50 pb-2">
                <span className="text-gray-500">Requested Product</span>
                <span className="col-span-2 font-medium">{lead.requested_product}</span>
              </div>
              <div className="grid grid-cols-3 border-b border-gray-50 pb-2">
                <span className="text-gray-500">Requested Plan ID</span>
                <span className="col-span-2 font-medium">{lead.requested_plan_id}</span>
              </div>
              <div className="grid grid-cols-3 pb-2">
                <span className="text-gray-500">Gateway ID</span>
                <span className="col-span-2 font-mono text-xs">{lead.gateway_id || 'Not generated yet'}</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-blue-900/5">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Status Controls</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Lead Status</label>
                <input 
                  type="text" 
                  value={leadStatus} 
                  onChange={e => setLeadStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">NMI Creation Status</label>
                <input 
                  type="text" 
                  value={nmiStatus} 
                  onChange={e => setNmiStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Banking Status</label>
                <input 
                  type="text" 
                  value={bankingStatus} 
                  onChange={e => setBankingStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Fee Schedule Status</label>
                <input 
                  type="text" 
                  value={feeScheduleStatus} 
                  onChange={e => setFeeScheduleStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Merchant Activation Status</label>
                <select 
                  value={merchantActivationStatus} 
                  onChange={e => setMerchantActivationStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 bg-white" 
                >
                  <option value="NOT_STARTED">NOT_STARTED</option>
                  <option value="IN_PROGRESS">IN_PROGRESS</option>
                  <option value="SUBMITTED_TO_PAYGENT">SUBMITTED_TO_PAYGENT</option>
                  <option value="APPROVED">APPROVED</option>
                  <option value="DECLINED">DECLINED</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Tap To Pay Status</label>
                <select 
                  value={tapToPayStatus} 
                  onChange={e => setTapToPayStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 bg-white" 
                >
                  <option value="PENDING_ENABLEMENT">PENDING_ENABLEMENT</option>
                  <option value="TERMS_REQUIRED">TERMS_REQUIRED</option>
                  <option value="ENABLED">ENABLED</option>
                  <option value="DECLINED">DECLINED</option>
                </select>
              </div>
              <div className="pt-4">
                <button 
                  onClick={handleSaveStatus}
                  disabled={saving}
                  className="w-full py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Update Statuses'}
                </button>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-blue-900/5 mt-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Tap to Pay Launch Tool</h3>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-xs text-amber-800 font-medium">⚠️ Do not mark Tap to Pay ENABLED until the merchant is approved, required terms are accepted, and the supported NMI Tap to Pay tool has been confirmed.</p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Launch Tool</label>
                <select 
                  value={tapToPayLaunchTool} 
                  onChange={e => setTapToPayLaunchTool(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 bg-white" 
                >
                  <option value="PENDING_CONFIRMATION">PENDING_CONFIRMATION</option>
                  <option value="NMI_CLOUD_COMMERCE">NMI_CLOUD_COMMERCE</option>
                  <option value="IPROCESS">IPROCESS</option>
                  <option value="MERCHANT_PORTAL">MERCHANT_PORTAL</option>
                  <option value="OTHER">OTHER</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Launch URL (Optional)</label>
                <input 
                  type="text" 
                  value={tapToPayLaunchUrl} 
                  onChange={e => setTapToPayLaunchUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500" 
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Admin Notes</label>
                <textarea 
                  value={tapToPayNotes} 
                  onChange={e => setTapToPayNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 min-h-[80px]" 
                  placeholder="Instructions for the merchant or admin notes..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Email Template & Handoff */}
        <div className="space-y-8">
          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-blue-900/5">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Onboarding Actions</h3>
              <div className="text-sm">
                <span className="text-gray-500 mr-2">Email Status:</span>
                <span className={`font-bold ${onboardingEmailStatus === 'SENT' ? 'text-green-600' : 'text-yellow-600'}`}>
                  {onboardingEmailStatus}
                </span>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-6">
              Use this email template to instruct the merchant on completing the secure onboarding process. Copying this template will automatically mark the email as SENT.
            </p>

            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 font-mono text-sm text-gray-800 whitespace-pre-wrap">
{`Subject: Complete Your Paygent Merchant Activation

Hi ${lead.first_name || lead.business_name},

Paygent received your request. Your Paygent Standard plan request has been recorded.

Tapn / Tap to Pay has been requested but requires Apple Pay / Google Pay / SoftPOS service enablement and terms acceptance.

Secure merchant activation will be completed through Paygent/NMI.
Do not send banking information by regular email.
Paygent will provide a secure completion method or portal instructions shortly.

If you need help, contact Paygent Support at support@gopaygent.com.

Welcome aboard!
The Paygent Team`}
            </div>

            <div className="mt-6 flex justify-between items-center">
              {lead.onboarding_email_sent_at && (
                <div className="text-xs text-gray-400">
                  Last sent: {new Date(lead.onboarding_email_sent_at).toLocaleString()}
                </div>
              )}
              <button 
                className="px-4 py-2 bg-blue-50 text-blue-700 font-bold rounded-lg hover:bg-blue-100 transition-colors ml-auto flex items-center gap-2"
                onClick={handleSendOnboardingInstructions}
              >
                <i className="ri-clipboard-line" /> Copy Template & Mark as Sent
              </button>
            </div>
          </div>

          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-blue-900/5">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Secure NMI Handoff</h3>
            <p className="text-sm text-gray-600 mb-6">
              When the merchant provides their banking information via the secure form, the system will hit the <code>/api/merchant/complete</code> endpoint to directly provision the NMI account. Do not use the public Get Started form for real banking details.
            </p>
            <div className="text-xs text-gray-400 bg-gray-50 p-4 rounded-xl border border-gray-200">
              <p>1. Merchant clicks link in email</p>
              <p>2. Merchant enters SSN/EIN, Routing, and Account #</p>
              <p>3. <code>POST /api/merchant/complete</code> sends data to NMI</p>
              <p>4. NMI responds with Gateway ID</p>
              <p>5. System automatically PATCHes Fee Schedule 679061</p>
            </div>
          </div>
          
          <div className="mt-4">
            <Link to="/admin/leads" className="text-gray-500 hover:text-gray-900 font-medium text-sm inline-flex items-center gap-1">
              <i className="ri-arrow-left-line" /> Back to Leads
            </Link>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
